import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/api/client";
import { getShadow } from "@/components/ui/ShadowBadge";
import { XPRing, getLevel } from "@/components/ui/XPBar";
import { useAuthStore } from "@/store/authStore";
import toast from "react-hot-toast";
import clsx from "clsx";

/* ── Helpers ──────────────────────────────────────────────────────────────── */
function timeAgo(date) {
  if (!date) return "Noch nie";
  const diff = Date.now() - new Date(date).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 2)   return "Gerade eben";
  if (m < 60)  return `vor ${m} Min.`;
  if (h < 24)  return `vor ${h} Std.`;
  if (d < 7)   return `vor ${d} Tag${d > 1 ? "en" : ""}`;
  return new Date(date).toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
}

/* ── Link child modal ─────────────────────────────────────────────────────── */
function LinkChildModal({ open, onClose, onSuccess }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCode = (val) => {
    // Auto-format: XXXX-XXXX-XXXX
    const clean = val.toUpperCase().replace(/[^A-Z0-9]/g, "");
    let formatted = clean;
    if (clean.length > 4)  formatted = clean.slice(0, 4) + "-" + clean.slice(4);
    if (clean.length > 8)  formatted = clean.slice(0, 4) + "-" + clean.slice(4, 8) + "-" + clean.slice(8);
    setCode(formatted.slice(0, 14));
  };

  const submit = async () => {
    if (code.replace(/-/g, "").length < 12) return toast.error("Bitte den vollständigen Code eingeben");
    setLoading(true);
    try {
      const { data } = await api.post("/students/me/redeem-parent-code", { code });
      toast.success(data.message);
      onSuccess();
      setCode("");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Fehler beim Einlösen");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-[#0d0d1f] border border-white/10 rounded-3xl p-6 space-y-5 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-violet-500/20 flex items-center justify-center text-xl">🔗</div>
            <div>
              <p className="font-black text-white text-sm">Kind verknüpfen</p>
              <p className="text-[10px] text-gray-500">Eltern-Einladungscode eingeben</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 transition-colors">✕</button>
        </div>

        {/* How it works */}
        <div className="bg-violet-500/10 border border-violet-500/20 rounded-2xl p-3">
          <p className="text-[11px] text-violet-300 font-bold mb-1">So funktioniert's:</p>
          <ol className="text-[10px] text-gray-400 space-y-1 list-decimal list-inside">
            <li>Dein Kind öffnet StudoX Flow auf seinem Gerät</li>
            <li>Geht zu Einstellungen → "Eltern-Einladungscode generieren"</li>
            <li>Gibt dir den 12-stelligen Code</li>
            <li>Du gibst den Code hier ein</li>
          </ol>
        </div>

        {/* Code input */}
        <div>
          <p className="text-xs text-gray-400 font-bold mb-2">Einladungscode</p>
          <input
            type="text"
            value={code}
            onChange={(e) => handleCode(e.target.value)}
            placeholder="XXXX-XXXX-XXXX"
            maxLength={14}
            className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-center font-mono font-black text-xl text-white tracking-widest placeholder-gray-600 focus:outline-none focus:border-violet-500 transition-colors"
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
          <p className="text-[10px] text-gray-600 text-center mt-1.5">{code.replace(/-/g, "").length}/12 Zeichen</p>
        </div>

        <button
          onClick={submit}
          disabled={loading || code.replace(/-/g, "").length < 12}
          className="w-full py-3.5 rounded-2xl font-black text-sm text-white transition-all disabled:opacity-40"
          style={{ background: "linear-gradient(135deg, #7c3aed, #5b21b6)", boxShadow: "0 4px 20px rgba(124,58,237,0.35)" }}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Verknüpfe…
            </span>
          ) : "🔗 Kind verknüpfen"}
        </button>
      </div>
    </div>
  );
}

/* ── Stat chip ────────────────────────────────────────────────────────────── */
function StatChip({ label, value, color }) {
  return (
    <div className="flex-1 bg-white/5 border border-white/10 rounded-xl p-2.5 text-center">
      <p className="font-black text-sm" style={{ color }}>{value}</p>
      <p className="text-[10px] text-gray-500 uppercase tracking-wide mt-0.5">{label}</p>
    </div>
  );
}

/* ── Activity bar ─────────────────────────────────────────────────────────── */
function ActivityBar({ label, value, max, color }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-gray-400">{label}</span>
        <span className="text-[11px] font-bold text-white">{value.toLocaleString("de-DE")}</span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

/* ── Child card ───────────────────────────────────────────────────────────── */
function ChildCard({ child }) {
  const level  = getLevel(child.xp);
  const shadow = getShadow(child.xp);
  const isOnline = child.lastLoginAt && (Date.now() - new Date(child.lastLoginAt).getTime()) < 300_000;

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-violet-500/40 transition-all duration-200">
      {/* Top accent line */}
      <div className="h-0.5 w-full" style={{ background: `linear-gradient(90deg, ${shadow.color}, #06b6d4)` }} />

      <div className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="relative flex-shrink-0">
            <XPRing xp={child.xp} size={68} />
            <div
              className="absolute -top-1.5 -right-1.5 w-9 h-9 rounded-xl bg-[#0d0d1f] border-2 flex items-center justify-center text-2xl"
              style={{ borderColor: shadow.color + "60" }}
            >
              {child.avatar || "🦊"}
            </div>
            {isOnline && (
              <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-400 border-2 border-[#0d0d1f]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-black text-base text-white truncate">{child.name}</h3>
              {child.streak > 0 && (
                <span className="text-xs font-bold text-orange-400">🔥{child.streak}</span>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-sm" style={{ color: shadow.color }}>{shadow.emoji}</span>
              <span className="text-xs font-bold" style={{ color: shadow.color }}>{shadow.name}</span>
              <span className="text-gray-600 text-[10px]">·</span>
              <span className="text-xs text-gray-500">Level {level}</span>
            </div>
            <p className="text-[10px] text-gray-600 mt-0.5">
              {isOnline ? <span className="text-green-400 font-bold">● Jetzt aktiv</span> : `Zuletzt: ${timeAgo(child.lastLoginAt)}`}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-2">
          <StatChip label="XP"     value={`⚡ ${(child.xp || 0).toLocaleString("de-DE")}`}    color="#8b5cf6" />
          <StatChip label="Coins"  value={`🪙 ${(child.coins || 0).toLocaleString("de-DE")}`} color="#fbbf24" />
          <StatChip label="Klasse" value={child.grade || "–"}                                   color="#06b6d4" />
        </div>

        {/* Progress bars */}
        <div className="space-y-2">
          <ActivityBar label="XP-Fortschritt" value={child.xp || 0} max={2000} color="#8b5cf6" />
          <ActivityBar label="Coins gesammelt" value={child.coins || 0} max={500} color="#fbbf24" />
        </div>

        {/* Recent achievements */}
        {child.recentAchievements?.length > 0 && (
          <div>
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Neueste Erfolge</p>
            <div className="flex gap-1.5 flex-wrap">
              {child.recentAchievements.slice(0, 4).map((ach) => (
                <div key={ach.id} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-white/5 border border-white/10">
                  <span className="text-sm">{ach.emoji}</span>
                  <span className="text-[10px] font-bold text-gray-400 truncate max-w-[80px]">{ach.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Shadow info */}
        <div className="flex items-center gap-2 p-2.5 rounded-xl border" style={{ background: `${shadow.color}10`, borderColor: `${shadow.color}30` }}>
          <span className="text-base">{shadow.emoji}</span>
          <div>
            <p className="text-[10px] font-black" style={{ color: shadow.color }}>{shadow.name}-Rang</p>
            <p className="text-[9px] text-gray-500">Nur für dich sichtbar — nicht für dein Kind</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Skeleton ─────────────────────────────────────────────────────────────── */
function Skeleton({ className }) {
  return <div className={clsx("animate-pulse rounded-xl bg-white/5", className)} />;
}

/* ── Main component ───────────────────────────────────────────────────────── */
export default function ParentDashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [linkOpen, setLinkOpen] = useState(false);
  const firstName = user?.name?.split(" ")[0] || "Elternteil";

  const { data: childrenData, isLoading } = useQuery({
    queryKey: ["parent-children"],
    queryFn: () => api.get("/students/me/children").then((r) => r.data),
    refetchInterval: 60_000,
  });

  const { data: convData } = useQuery({
    queryKey: ["conversations"],
    queryFn: () => api.get("/messages/conversations").then((r) => r.data),
  });

  const children  = childrenData?.children || [];
  const convos    = convData?.conversations || [];
  const unreadSum = convos.reduce((a, c) => a + (c.unreadCount || 0), 0);
  const canAddMore = children.length < 2;

  return (
    <div className="space-y-6 animate-fade-in pb-8 max-w-2xl mx-auto">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest">Eltern-Portal</p>
          <h1 className="text-xl font-black flex items-center gap-2 mt-0.5 text-white">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="#8b5cf6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            Willkommen, {firstName}
          </h1>
          <p className="text-xs text-gray-500 mt-0.5 ml-8">
            {children.length === 0 ? "Noch kein Kind verknüpft" : `${children.length} Kind${children.length > 1 ? "er" : ""} verknüpft`}
          </p>
        </div>
        {/* Messages badge */}
        <button
          onClick={() => navigate("/messages")}
          className="relative w-11 h-11 rounded-2xl flex items-center justify-center transition-all hover:scale-105 active:scale-95 flex-shrink-0"
          style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.3)" }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="#a78bfa" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          {unreadSum > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-violet-600 border-2 border-[#060614] text-[9px] font-black text-white flex items-center justify-center">
              {unreadSum > 9 ? "9+" : unreadSum}
            </span>
          )}
        </button>
      </div>

      {/* ── Link child CTA (shown when 0 or 1 children) ─────────────────────── */}
      {canAddMore && (
        <button
          onClick={() => setLinkOpen(true)}
          className="w-full flex items-center gap-4 p-4 rounded-2xl border transition-all hover:scale-[1.01] active:scale-[0.99] group"
          style={{
            background: children.length === 0
              ? "linear-gradient(135deg, rgba(124,58,237,0.15) 0%, rgba(6,182,212,0.07) 100%)"
              : "rgba(255,255,255,0.04)",
            borderColor: children.length === 0 ? "rgba(124,58,237,0.4)" : "rgba(255,255,255,0.1)",
          }}
        >
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0 group-hover:scale-110 transition-transform"
            style={{ background: "rgba(139,92,246,0.2)", border: "1px solid rgba(139,92,246,0.35)" }}
          >
            {children.length === 0 ? "🔗" : "➕"}
          </div>
          <div className="flex-1 text-left">
            <p className="font-black text-sm text-white">
              {children.length === 0 ? "Kind verknüpfen" : "Weiteres Kind hinzufügen"}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {children.length === 0
                ? "Gib den Einladungscode deines Kindes ein"
                : `Noch ${2 - children.length} weiteres Kind möglich`}
            </p>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      )}

      {/* ── No children state ────────────────────────────────────────────────── */}
      {!isLoading && children.length === 0 && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center text-4xl" style={{ background: "rgba(124,58,237,0.12)" }}>
            👨‍👧
          </div>
          <div>
            <p className="text-sm font-black text-white">Noch keine Kinder verknüpft</p>
            <p className="text-xs text-gray-500 mt-1.5 leading-relaxed max-w-xs mx-auto">
              Dein Kind muss in seinen <strong className="text-gray-300">Einstellungen</strong> einen Eltern-Einladungscode erstellen.
              Den Code gibst du dann hier ein.
            </p>
          </div>
          <button
            onClick={() => setLinkOpen(true)}
            className="px-6 py-2.5 rounded-xl font-black text-sm text-white transition-all hover:scale-105"
            style={{ background: "linear-gradient(135deg, #7c3aed, #5b21b6)" }}
          >
            🔗 Code eingeben
          </button>
        </div>
      )}

      {/* ── Loading ──────────────────────────────────────────────────────────── */}
      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-56 w-full" />
          <Skeleton className="h-56 w-full" />
        </div>
      )}

      {/* ── Children list ────────────────────────────────────────────────────── */}
      {children.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-black text-white flex items-center gap-2">
              <span>👨‍👧</span> Meine Kinder
            </h2>
            <span className="text-[10px] font-bold text-gray-600 bg-white/5 border border-white/10 rounded-full px-2.5 py-1">
              {children.length}/2
            </span>
          </div>
          <div className="space-y-4">
            {children.map((child) => (
              <ChildCard key={child.id} child={child} />
            ))}
          </div>
        </section>
      )}

      {/* ── Messages shortcut ────────────────────────────────────────────────── */}
      <button
        onClick={() => navigate("/messages")}
        className="w-full flex items-center gap-4 p-4 rounded-2xl border transition-all hover:scale-[1.01] active:scale-[0.99]"
        style={{ background: "linear-gradient(135deg, rgba(124,58,237,0.10) 0%, rgba(6,182,212,0.05) 100%)", borderColor: "rgba(124,58,237,0.25)" }}
      >
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(124,58,237,0.18)" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="#a78bfa" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </div>
        <div className="flex-1 text-left">
          <p className="font-black text-sm text-white">Nachrichten</p>
          <p className="text-xs text-gray-500">Mit Lehrkräften &amp; Mentoren kommunizieren</p>
        </div>
        {unreadSum > 0 && (
          <span className="bg-violet-600 text-white text-[10px] font-black rounded-full w-6 h-6 flex items-center justify-center">
            {unreadSum > 9 ? "9+" : unreadSum}
          </span>
        )}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>

      {/* ── Info box ─────────────────────────────────────────────────────────── */}
      <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-2xl p-4 space-y-2">
        <p className="text-[10px] font-black text-cyan-400 uppercase tracking-widest mb-2">Für Eltern</p>
        {[
          ["📊", "Echtzeit-Lernfortschritt deines Kindes auf einen Blick"],
          ["💬", "Direkter Kontakt mit Lehrkräften und Mentoren"],
          ["🔐", "Der Shadow-Rang ist intern — wird dem Kind nicht angezeigt"],
          ["🔗", "Maximal 2 Kinder können verknüpft werden"],
        ].map(([emoji, text]) => (
          <div key={text} className="flex items-start gap-2">
            <span className="text-sm flex-shrink-0">{emoji}</span>
            <p className="text-xs text-gray-500 leading-relaxed">{text}</p>
          </div>
        ))}
      </div>

      {/* ── Link modal ───────────────────────────────────────────────────────── */}
      <LinkChildModal
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        onSuccess={() => qc.invalidateQueries({ queryKey: ["parent-children"] })}
      />
    </div>
  );
}
